import React, { useState } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Switch, ScrollView } from "react-native";
import { RouteProp, useRoute } from "@react-navigation/native";
import { observer } from "mobx-react";
import { bannerStore } from "../stores/BannerStore";
import { IBanner } from "@interfaces/ibanner";
import { EnumRoutesAppWeb } from "../enum/EnumRoutesAppWeb";
import { Theme } from "../enum/Theme";
import { goBack } from "../services/NavigationService";
import { uuidv4 } from "@utils/idUtils";
import PhotoUploader from "./PhotoUploader";
import { IMedia } from "@interfaces/imedia";
import { Language } from "@constants/language";
import { LanguageSelector } from "../comps/LanguageSelector";

type BannerFormRouteProp = RouteProp<{ [EnumRoutesAppWeb.createEditBanner]: { banner?: IBanner } }, typeof EnumRoutesAppWeb.createEditBanner>;

const BannerForm = observer(() => {
  const route = useRoute<BannerFormRouteProp>();
  const banner = route.params?.banner;

  const [lang, setLang] = useState<Language>(banner?.lang ?? Language.PT);
  const [title, setTitle] = useState(banner?.title || "");
  const [subtitle, setSubtitle] = useState(banner?.subtitle || "");
  const [medias, setMedias] = useState<IMedia[]>(banner?.medias || []);
  const [gradientColors, setGradientColors] = useState<string[]>(banner?.gradientColors || ['#A0BC44', '#417A32']);
  const [textColor, setTextColor] = useState(banner?.textColor || '#ffffff');
  const [linkType, setLinkType] = useState<'none' | 'route' | 'url'>(banner?.linkType || 'none');
  const [linkValue, setLinkValue] = useState(banner?.linkValue || "");
  const [isActive, setIsActive] = useState(banner?.isActive ?? true);
  const [position, setPosition] = useState<'top' | 'bottom' | 'middle'>(banner?.position || 'bottom');
  const [targetScreens, setTargetScreens] = useState<string[]>(banner?.targetScreens || ['alimentacao']);
  const [priority, setPriority] = useState(banner?.priority || 1);

  const availableScreens = [
    'alimentacao',
    'sono', 
    'sons',
    'desenvolver',
    'home',
    'relatorios',
    'options',
    'blog',
    '*'
  ];

  const handleSave = async () => {
    const data: IBanner = {
      id: banner?.id || uuidv4(),
      lang,
      title,
      subtitle,
      medias,
      gradientColors: gradientColors as [string, string, ...string[]],
      textColor,
      linkType,
      linkValue,
      isActive,
      position,
      targetScreens,
      priority,
      createdAt: banner?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await bannerStore.saveBanner(data);
    goBack();
  };

  const toggleScreen = (screen: string) => {
    setTargetScreens(prev => 
      prev.includes(screen) 
        ? prev.filter(s => s !== screen)
        : [...prev, screen]
    );
  };

  const addGradientColor = () => {
    setGradientColors(prev => [...prev, '#000000']);
  };

  const updateGradientColor = (index: number, color: string) => {
    setGradientColors(prev => prev.map((c, i) => i === index ? color : c));
  };

  const removeGradientColor = (index: number) => {
    if (gradientColors.length > 2) {
      setGradientColors(prev => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>{banner ? "Editar Banner" : "Novo Banner"}</Text>

      <Text style={styles.section}>Título:</Text>
      <TextInput
        placeholder="Digite o título do banner"
        value={title}
        onChangeText={setTitle}
        style={styles.input}
      />

      <LanguageSelector value={lang} onChange={setLang} />

      <Text style={styles.section}>Subtítulo:</Text>
      <TextInput
        placeholder="Digite o subtítulo (opcional)"
        value={subtitle}
        onChangeText={setSubtitle}
        style={styles.input}
      />

      <Text style={styles.section}>Imagem:</Text>
      <PhotoUploader
        postId={banner?.id || "tempBanner"}
        media={medias}
        setMedia={setMedias}
        mainMediaUrl={medias?.[0]?.url || ""}
        setMainMediaUrl={() => {}}
        showAudio={false}
        showVideo={false}
      />

      <Text style={styles.section}>Cores do Gradiente:</Text>
      {gradientColors.map((color, index) => (
        <View key={index} style={styles.colorRow}>
          <input
            type="color"
            value={color}
            onChange={(e) => updateGradientColor(index, e.target.value)}
            style={styles.colorPicker}
          />
          <TextInput
            value={color}
            onChangeText={(text) => updateGradientColor(index, text)}
            style={[styles.input, styles.colorInput]}
            placeholder="#000000"
          />
          {gradientColors.length > 2 && (
            <TouchableOpacity 
              onPress={() => removeGradientColor(index)}
              style={styles.removeButton}
            >
              <Text style={styles.removeButtonText}>Remover</Text>
            </TouchableOpacity>
          )}
        </View>
      ))}
      <TouchableOpacity onPress={addGradientColor} style={styles.addColorButton}>
        <Text style={styles.addColorButtonText}>Adicionar Cor</Text>
      </TouchableOpacity>

      <Text style={styles.section}>Cor do Texto:</Text>
      <View style={styles.colorRow}>
        <input
          type="color"
          value={textColor}
          onChange={(e) => setTextColor(e.target.value)}
          style={styles.colorPicker}
        />
        <TextInput
          placeholder="#ffffff"
          value={textColor}
          onChangeText={setTextColor}
          style={[styles.input, styles.colorInput]}
        />
      </View>

      <Text style={styles.section}>Tipo de Link:</Text>
      <View style={styles.linkTypeContainer}>
        <TouchableOpacity 
          style={[styles.linkTypeButton, linkType === 'none' && styles.linkTypeButtonActive]}
          onPress={() => setLinkType('none')}
        >
          <Text style={[styles.buttonText, linkType === 'none' && styles.buttonTextActive]}>Sem link</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.linkTypeButton, linkType === 'route' && styles.linkTypeButtonActive]}
          onPress={() => setLinkType('route')}
        >
          <Text style={[styles.buttonText, linkType === 'route' && styles.buttonTextActive]}>Rota interna</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.linkTypeButton, linkType === 'url' && styles.linkTypeButtonActive]}
          onPress={() => setLinkType('url')}
        >
          <Text style={[styles.buttonText, linkType === 'url' && styles.buttonTextActive]}>URL externa</Text>
        </TouchableOpacity>
      </View>

      {linkType !== 'none' && (
        <>
          <Text style={styles.section}>
            {linkType === 'route' ? 'Rota:' : 'URL:'}
          </Text>
          <TextInput
            placeholder={linkType === 'route' ? 'EnumRoutes.tabBlog' : 'https://exemplo.com'}
            value={linkValue}
            onChangeText={setLinkValue}
            style={styles.input}
          />
        </>
      )}

      <Text style={styles.section}>Posição:</Text>
      <View style={styles.positionContainer}>
        <TouchableOpacity 
          style={[styles.positionButton, position === 'top' && styles.positionButtonActive]}
          onPress={() => setPosition('top')}
        >
          <Text style={[styles.buttonText, position === 'top' && styles.buttonTextActive]}>Topo</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.positionButton, position === 'middle' && styles.positionButtonActive]}
          onPress={() => setPosition('middle')}
        >
          <Text style={[styles.buttonText, position === 'middle' && styles.buttonTextActive]}>Meio</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.positionButton, position === 'bottom' && styles.positionButtonActive]}
          onPress={() => setPosition('bottom')}
        >
          <Text style={[styles.buttonText, position === 'bottom' && styles.buttonTextActive]}>Rodapé</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.section}>Prioridade:</Text>
      <TextInput
        placeholder="1"
        value={priority.toString()}
        onChangeText={(text) => setPriority(parseInt(text) || 1)}
        style={styles.input}
        keyboardType="numeric"
      />

      <Text style={styles.section}>Telas de Destino:</Text>
      <View style={styles.screensContainer}>
        {availableScreens.map(screen => (
          <TouchableOpacity 
            key={screen}
            style={[styles.screenButton, targetScreens.includes(screen) && styles.screenButtonActive]}
            onPress={() => toggleScreen(screen)}
          >
            <Text style={[styles.buttonText, targetScreens.includes(screen) && styles.buttonTextActive]}>
              {screen === '*' ? 'Todas as telas' : screen}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.switchContainer}>
        <Text>Banner Ativo:</Text>
        <Switch value={isActive} onValueChange={setIsActive} />
      </View>

      <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
        <Text style={styles.saveButtonText}>Salvar</Text>
      </TouchableOpacity>
    </ScrollView>
  );
});

export default BannerForm;

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  title: { fontSize: 22, fontWeight: "bold", marginBottom: 20 },
  section: { marginTop: 15, fontWeight: "bold", marginBottom: 5 },
  input: { borderWidth: 1, borderColor: "#ccc", borderRadius: 5, padding: 10, marginBottom: 10 },
  colorRow: { flexDirection: "row", alignItems: "center", marginBottom: 5 },
  colorPicker: { width: 50, height: 40, borderRadius: 4, marginRight: 10 },
  colorInput: { flex: 1, marginRight: 10 },
  removeButton: { backgroundColor: "#f44336", padding: 8, borderRadius: 4 },
  removeButtonText: { color: "#fff", fontSize: 12 },
  addColorButton: { backgroundColor: Theme.foreground, padding: 10, borderRadius: 5, marginBottom: 10 },
  addColorButtonText: { color: "#fff", textAlign: "center" },
  linkTypeContainer: { flexDirection: "row", marginBottom: 10 },
  linkTypeButton: { flex: 1, padding: 10, borderWidth: 1, borderColor: "#ccc", alignItems: "center", marginRight: 5 },
  linkTypeButtonActive: { backgroundColor: Theme.foreground },
  positionContainer: { flexDirection: "row", marginBottom: 10 },
  positionButton: { flex: 1, padding: 10, borderWidth: 1, borderColor: "#ccc", alignItems: "center", marginRight: 5 },
  positionButtonActive: { backgroundColor: Theme.foreground },
  screensContainer: { flexDirection: "row", flexWrap: "wrap", marginBottom: 10 },
  screenButton: { padding: 8, borderWidth: 1, borderColor: "#ccc", borderRadius: 4, margin: 2 },
  screenButtonActive: { backgroundColor: Theme.foreground },
  buttonText: { color: "#333" },
  buttonTextActive: { color: "#fff" },
  switchContainer: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  saveButton: { backgroundColor: Theme.foreground, padding: 15, borderRadius: 5 },
  saveButtonText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
});
